import { Send, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useState } from 'react';
import Navbar from './landing-pages/Navbar';
import '../styles/components/contactUs.scss';
import logoSymbol from "../assets/logoSymbol.svg";
import { mainUrl } from '../constant'

// Form step types
type FormStep = 'info' | 'message' | 'success';

// Custom confirmation messages based on inquiry type
const confirmationMessages = {
    'General Question': "We've received your question and our team will get back to you within 1-2 business days.",
    'Partnership': "Thanks for your interest in partnering with QuickCruit! Our partnership team will review your inquiry and contact you shortly.",
    'Support': "Your support request has been received. Our customer support team typically responds within 24 hours.",
    'Feedback': "Thank you for your valuable feedback! We appreciate you taking the time to help us improve QuickCruit.",
    'default': "Thank you for reaching out. We'll get back to you shortly."
};

// Success message component with explicit styling to avoid green background
const SuccessMessage = ({ message }: { message: string }) => (
    <div className="success-message">
        <h2 style={{ marginBottom: '1.5rem', color: '#1a1a1a' }}>
            Thank you for your message!
        </h2>
        <div style={{ 
            color: '#666',
            lineHeight: '1.5',
            fontSize: '1.05rem',
            maxWidth: '450px',
            margin: '0 auto',
            backgroundColor: 'transparent',
            border: 'none',
            padding: 0
        }}>
            {message}
        </div>
    </div>
);

// QuickCruit Logo component with blue styling
const QuickCruitLogo = () => (
    <div className="quickcruit-logo-wrapper">
        <svg width="90" height="90" viewBox="0 0 44 42" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="path-1-inside-1_2_24" fill="white">
                <path d="M34.8912 23.6176C34.8912 33.2525 27.0806 41.0632 17.4456 41.0632C7.81067 41.0632 0 33.2525 0 23.6176C0 13.9826 7.81067 6.17194 17.4456 6.17194C27.0806 6.17194 34.8912 13.9826 34.8912 23.6176ZM2.55847 23.6176C2.55847 31.8395 9.22368 38.5047 17.4456 38.5047C25.6676 38.5047 32.3328 31.8395 32.3328 23.6176C32.3328 15.3956 25.6676 8.73041 17.4456 8.73041C9.22368 8.73041 2.55847 15.3956 2.55847 23.6176Z"/>
            </mask>
            <path d="M34.8912 23.6176C34.8912 33.2525 27.0806 41.0632 17.4456 41.0632C7.81067 41.0632 0 33.2525 0 23.6176C0 13.9826 7.81067 6.17194 17.4456 6.17194C27.0806 6.17194 34.8912 13.9826 34.8912 23.6176ZM2.55847 23.6176C2.55847 31.8395 9.22368 38.5047 17.4456 38.5047C25.6676 38.5047 32.3328 31.8395 32.3328 23.6176C32.3328 15.3956 25.6676 8.73041 17.4456 8.73041C9.22368 8.73041 2.55847 15.3956 2.55847 23.6176Z" fill="#0033A0" stroke="#0033A0" stroke-width="2" mask="url(#path-1-inside-1_2_24)"/>
            <path d="M39 19.9239C39 24.3369 34.8512 27.9197 29.7568 27.9197C24.6625 27.9197 20.5137 24.3369 20.5137 19.9239C20.5137 15.5109 24.6625 11.9282 29.7568 11.9282C34.8512 11.9282 39 15.5109 39 19.9239Z" fill="#0033A0"/>
            <path d="M25.2842 23.8169V18.0535L26.7173 16.5599V23.8169H25.2842Z" fill="white"/>
            <path d="M28.291 23.8169V16.5599L29.72 16.5569V22.3307L33.2842 18.5627V21.0399L28.291 23.8169Z" fill="white"/>
        </svg>
    </div>
);

const ContactUs = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [currentStep, setCurrentStep] = useState<FormStep>('info');
    const [fadeClass, setFadeClass] = useState('fade-in');
    const [selectedReason, setSelectedReason] = useState('');
    const [titleFade, setTitleFade] = useState('fade-in');
    const [showTitle, setShowTitle] = useState(true);
    const [showLogo, setShowLogo] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            const response = await fetch(`${mainUrl}contact/contact-us`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    message: formData.message,
                    subject: "Contact Us for Quickcruit"
                })
            });

            if (!response.ok) {
                // Handle HTTP errors (e.g., response.status is 4xx or 5xx)
                const errorData = await response.json().catch(() => ({ message: 'Submission failed and error response was not valid JSON.' }));
                console.error('Form submission error:', response.status, errorData);
                // Optionally, display an error message to the user here
                // For now, we'll prevent proceeding to the success screen on error
                return; 
            }
            // If submission is successful, proceed with UI changes:
            setTitleFade('fade-out');
            setFadeClass('fade-out');
            
            
            setTimeout(() => {
                setShowTitle(false);
                setShowLogo(true);
                setCurrentStep('success');
                setFadeClass('fade-in');
            }, 400);

        } catch (error) {
            // Handle network errors or other issues with the fetch call
            console.error('Network error or other issue during form submission:', error);
            // Optionally, display an error message to the user here
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const nextStep = () => {
        if (currentStep === 'info') {
            if (!formData.name || !formData.email) {
                return; // Don't proceed if required fields aren't filled
            }
            setFadeClass('fade-out');
            setTimeout(() => {
                setCurrentStep('message');
                setFadeClass('fade-in');
            }, 400);
        }
    };

    const prevStep = () => {
        if (currentStep === 'message') {
            setFadeClass('fade-out');
            setTimeout(() => {
                setCurrentStep('info');
                setFadeClass('fade-in');
            }, 400);
        }
    };

    const handleReasonSelect = (reason: string) => {
        setSelectedReason(reason);
        setFormData({
            ...formData,
            subject: reason
        });
    };

    // Get the appropriate confirmation message based on selected reason
    const getConfirmationMessage = () => {
        return confirmationMessages[selectedReason as keyof typeof confirmationMessages] || confirmationMessages.default;
    };

    return (
        <div className="contact-us-container">
            <Navbar />
            
            <div className="contact-page-content">
                {/* Floating accent elements with updated emojis */}
                <div className="floating-accent rocket">
                    <span>👨‍💻</span>
                </div>
                <div className="floating-accent chat">
                    <span>💬</span>
                </div>
                <div className="floating-accent sparkle">
                    <span>💡</span>
                </div>
                
                {/* Title area - shows either text or logo */}
                <div className="title-area">
                    {showTitle && (
                        <div className={`page-title ${titleFade}`}>
                            <h1>Let's connect</h1>
                            <p>Have questions? We'd love to hear from you.</p>
                        </div>
                    )}
                    
                    {showLogo && (
                        <div className="logo-container-header">
                            <img src={logoSymbol} alt="QuickCruit Logo" className="header-logo" />
                        </div>
                    )}
                </div>
                
                <div className="card-container">
                    <div className={`fade-container ${fadeClass}`}>
                        <div className="contact-card">
                            {currentStep === 'info' && (
                                <div>
                                    <h2 className="step-title">Let's get to know you <span>👋</span></h2>
                                    
                                    <div className="form-content">
                                        <div className="form-field">
                                            <label htmlFor="name">Your Name</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter your name"
                                            />
                                        </div>

                                        <div className="form-field">
                                            <label htmlFor="email">Email Address</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter your email"
                                            />
                                        </div>
                                        
                                        <div className="option-bubbles-field">
                                            <label>What best explains your inquiry?</label>
                                            <div className="option-bubbles-container">
                                                {['General Question', 'Partnership', 'Support', 'Feedback'].map((reason) => (
                                                    <button
                                                        key={reason}
                                                        type="button"
                                                        onClick={() => handleReasonSelect(reason)}
                                                        className={`option-bubble ${selectedReason === reason ? 'selected' : ''}`}
                                                    >
                                                        {reason}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="form-actions">
                                        <div></div> {/* Empty div for flex alignment */}
                                        <button
                                            type="button"
                                            onClick={nextStep}
                                            disabled={!formData.name || !formData.email}
                                            className="save-button"
                                        >
                                            <span>Next</span>
                                            <ChevronRight size={18} />
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            {currentStep === 'message' && (
                                <div>
                                    <h2 className="step-title">Tell us more <span>💬</span></h2>
                                    
                                    <form onSubmit={handleSubmit}>
                                        <div className="form-field">
                                            <label htmlFor="message">Your message</label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                rows={5}
                                                required
                                                placeholder="Tell us how we can help you..."
                                            ></textarea>
                                        </div>
                                        
                                        <div className="form-actions">
                                            <button
                                                type="button"
                                                onClick={prevStep}
                                                className="cancel-button"
                                            >
                                                <ChevronLeft size={18} />
                                                <span>Back</span>
                                            </button>
                                            
                                            <button
                                                type="submit"
                                                className="save-button"
                                            >
                                                <Send size={18} />
                                                <span>Send Message</span>
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                            
                            {currentStep === 'success' && (
                                <SuccessMessage message={getConfirmationMessage()} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactUs;